'use client';

import { createFileRoute } from '@tanstack/react-router';
import { PERMISSION_LABELS, PermissionLabel, PermissionValue } from '@repo/shared/domain';
import GeneralStatusError from 'src/components/StatusError/General';
import StatusError from 'src/components/StatusError';

export const Route = createFileRoute('/403')({
	validateSearch: (search: Record<string, unknown>) => {
		return {
			permissions: (search.permissions as string | string[]) || undefined,
			occurredAt: (search.occurredAt as string) || undefined,
			method: (search.method as string) || undefined,
			uuid: (search.uuid as string) || undefined,
		};
	},
	component: ForbiddenPage,
});

function getPermissionAsLabel(permission: PermissionValue): PermissionLabel {
	return PERMISSION_LABELS[permission] || permission;
}

function ForbiddenPage() {
	const { permissions = [], occurredAt, method, uuid } = Route.useSearch();

	const permissionsAsArray = Array.isArray(permissions) ? permissions : permissions ? permissions.split(',') : [];

	return (
		<GeneralStatusError
			label="Action Not Allowed"
			actions={[
				{
					label: 'Go back to previous page',
					onClick: () => {
						const hasLocalHistory = document.referrer && new URL(document.referrer).origin === window.location.origin;
						if (hasLocalHistory) {
							window.history.back();
						} else {
							window.location.href = '/';
						}
					},
				},
				{
					label: 'Go to login page',
					href: '/authentication/login',
					variant: 'outlined',
				},
			]}
			occurredAt={occurredAt}
			method={method}
			uuid={uuid}
		>
			<StatusError.Section>
				{permissionsAsArray.length > 0 && (
					<div>
						<StatusError.Section.Title>Missing Permissions:</StatusError.Section.Title>
						<StatusError.Section.Text>
							None of the following permissions are assigned to any of your roles:
						</StatusError.Section.Text>
						<StatusError.Section.BubbleWrap>
							{permissionsAsArray.map((permission) => (
								<StatusError.Section.Bubble key={permission}>
									{getPermissionAsLabel(permission as PermissionValue)}
								</StatusError.Section.Bubble>
							))}
						</StatusError.Section.BubbleWrap>
					</div>
				)}

				<p className="text-gray-600 text-sm">
					If you believe this is an error, please contact the site administrator to add the listed permissions to one of
					your roles. You can also try to login with a different account with a different role if you have one.
				</p>
			</StatusError.Section>
		</GeneralStatusError>
	);
}
